import { useCallback, useMemo } from 'react'
import { useSidebarContext } from '../context'
import { COLLAPSED_WIDTH, SIDEBAR_WIDTH } from '../constants'

interface UseSidebarStateReturn {
  readonly isExpanded: boolean
  readonly isPinned: boolean
  readonly isHovering: boolean
  readonly shouldShowExpanded: boolean
  readonly currentWidth: number
  readonly toggleExpand: () => void
  readonly togglePin: () => void
  readonly expand: () => void
  readonly collapse: () => void
}

export function useSidebarState(): UseSidebarStateReturn {
  const { state, toggleExpand, setPinned, setHovering } = useSidebarContext()
  const { isExpanded, isPinned, isHovering } = state

  const shouldShowExpanded = useMemo(
    () => isExpanded || (isHovering && !isPinned),
    [isExpanded, isHovering, isPinned],
  )

  const currentWidth = useMemo(
    () => ((isPinned && isExpanded) || shouldShowExpanded ? SIDEBAR_WIDTH : COLLAPSED_WIDTH),
    [isPinned, isExpanded, shouldShowExpanded],
  )

  const togglePin = useCallback(() => {
    const newPinned = !isPinned
    setPinned(newPinned)
    if (newPinned) {
      setHovering(false)
    }
  }, [isPinned, setPinned, setHovering])

  const expand = useCallback(() => {
    if (!isExpanded) {
      toggleExpand()
    }
  }, [isExpanded, toggleExpand])

  const collapse = useCallback(() => {
    if (isExpanded && !isPinned) {
      toggleExpand()
    }
  }, [isExpanded, isPinned, toggleExpand])

  return {
    isExpanded,
    isPinned,
    isHovering,
    shouldShowExpanded,
    currentWidth,
    toggleExpand,
    togglePin,
    expand,
    collapse,
  } as const
}
